var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { AwesomeEvent, ComponentAPI } from '@philips-internal/oswald-ui-core';
import { ClickEventListener } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { getActualPageCount } from './trail-utils.js';
class TrailAPI extends ComponentAPI {
    constructor() {
        super(...arguments);
        /** @event */
        this.onNewCardClick = new ClickEventListener();
        /** @event */
        this.onSeeAllClick = new ClickEventListener();
        /** @event */
        this.onClick = new ClickEventListener();
        /** @event */
        this.onDoubleClick = new ClickEventListener();
        /** @event */
        this.onContextMenuClick = new ClickEventListener();
    }
    /**
     * Returns if the "Add new" card is being shown.
     *
     * @returns a boolean indicating whether the "Add new" card is being shown or not.
     */
    isCanAddNewItem() {
        return this.state.canAddNewItem;
    }
    /**
     * Defines whether the "Add new" card should be shown.
     *
     * @param canAddNewItem - Indicates if it should be shown or not.
     */
    setCanAddNewItem(canAddNewItem) {
        this.setState({ canAddNewItem });
    }
    /**
     * Get the current data of the trail.
     *
     * @returns the current data of the trail.
     */
    getData() {
        return this.state.data;
    }
    /**
     * Defines the data of the trail.
     *
     * @param data - The data for the trail.
     */
    setData(data) {
        this.setState({ data });
    }
    /**
     * Returns if the dates of the cards are being shown.
     *
     * @returns a boolean indicating whether the dates of the cards are being shown or not.
     */
    isDateVisible() {
        return this.state.dateVisible;
    }
    /**
     * Defines whether the dates of the cards should be shown.
     *
     * @param dateVisible - If the dates of the cards should be shown or not.
     */
    setDateVisible(dateVisible) {
        this.setState({ dateVisible });
    }
    /**
     * Get the current subtitle of the no data card.
     *
     * @returns the current subtitle of the no data card.
     */
    getEmptyResultSubtitle() {
        return this.state.emptyResultSubtitle;
    }
    /**
     * Defines the subtitle for the card when there are no data in the trail.
     *
     * @param emptyResultSubtitle - Subtitle for the no data card.
     */
    setEmptyResultSubtitle(emptyResultSubtitle) {
        this.setState({ emptyResultSubtitle });
    }
    /**
     * Returns the current title of the no data card.
     *
     * @returns the current title of the no data card.
     */
    getEmptyResultTitle() {
        return this.state.emptyResultTitle;
    }
    /**
     * Defines the title for the card when there are no data in the trail.
     *
     * @param emptyResultTitle - Title for the no data card.
     */
    setEmptyResultTitle(emptyResultTitle) {
        this.setState({ emptyResultTitle });
    }
    /**
     * Returns the current number of items per page.
     *
     * @returns the current number of items per page.
     */
    getItemsPerPage() {
        return this.state.itemsPerPage;
    }
    /**
     * Defines how many items will be shown per page.
     *
     * @param itemsPerPage - The number of items per page.
     */
    setItemsPerPage(itemsPerPage) {
        const { canAddNewItem, data, page } = this.state;
        const actualPageCount = getActualPageCount({ canAddNewItem, data, itemsPerPage });
        const currentPage = Math.min(page, actualPageCount);
        this.setState({ itemsPerPage, page: currentPage });
    }
    /**
     * Returns if the loader is being shown or not.
     *
     * @returns a boolean indicating whether the loader is being shown or not.
     */
    isLoading() {
        return this.state.loading;
    }
    /**
     * Defines if the data of the trail is still loading.
     *
     * @param loading - If the loader should be shown or not.
     */
    setLoading(loading) {
        this.setState({ loading });
    }
    /**
     * Returns the current text of the "Add new" card.
     *
     * @returns the current the of the "Add new" card.
     */
    getNewItemText() {
        return this.state.newItemText;
    }
    /**
     * Defines the text of the "Add new" card in the trail.
     *
     * @param newItemText - New text for the "Add new" card.
     */
    setNewItemText(newItemText) {
        this.setState({ newItemText });
    }
    /**
     * Returns the current text of the "See all" link.
     *
     * @returns the current text of the "See all" link.
     */
    getSeeAll() {
        return this.state.seeAll;
    }
    /**
     * Defines the text of the "See all" link.
     *
     * @param seeAll - New text for the "See all" link.
     */
    setSetAll(seeAll) {
        this.setState({ seeAll });
    }
    /**
     * Returns if the "See all" link is visible.
     *
     * @returns a boolean indicating if it is visible or not.
     */
    isShowSeeAll() {
        return this.state.showSeeAll;
    }
    /**
     * Defines if the "See all" link will be shown.
     *
     * @param showSeeAll - If it should be visible or not.
     */
    setShowSeeAll(showSeeAll) {
        this.setState({ showSeeAll });
    }
    /**
     * Returns the current title of the trail.
     *
     * @returns the current title of the trail.
     */
    getTitle() {
        return this.state.title;
    }
    /**
     * Defines the title of the trail.
     *
     * @param title - New title for the trail.
     */
    setTitle(title) {
        this.setState({ title });
    }
    /**
     * Navigates to the first page in the trail.
     */
    firstPage() {
        this.setState({ page: 0 });
    }
    /**
     * Navigates to the last page in the trail.
     */
    lastPage() {
        const { canAddNewItem, data, itemsPerPage } = this.state;
        const actualPageCount = getActualPageCount({ canAddNewItem, data, itemsPerPage });
        this.setState({ page: actualPageCount });
    }
    /**
     * Navigates to the next page in the trail if it exists.
     */
    nextPage() {
        const { page, canAddNewItem, data, itemsPerPage } = this.state;
        const actualPageCount = getActualPageCount({ canAddNewItem, data, itemsPerPage });
        const nextPage = page + 1;
        if (nextPage <= actualPageCount) {
            this.setState({ page: nextPage });
        }
    }
    /**
     * Navigates to the previous page in the trail if it exists.
     */
    previousPage() {
        const { page } = this.state;
        const nextPage = page - 1;
        if (nextPage >= 0) {
            this.setState({ page: nextPage });
        }
    }
}
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], TrailAPI.prototype, "onNewCardClick", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], TrailAPI.prototype, "onSeeAllClick", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], TrailAPI.prototype, "onClick", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], TrailAPI.prototype, "onDoubleClick", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], TrailAPI.prototype, "onContextMenuClick", void 0);
export default TrailAPI;
//# sourceMappingURL=trail-api.js.map